/**
 * AetherLite — catálogo de PLANTILLAS (15) para arrancar un plan.
 *
 * Cada plantilla es **code-as-data** en TS: define paleta + tipografía + un
 * `applyDefaults(plan)` que llena el `PlanDoc` con contenido temático coherente
 * con la categoría (rutina, cardio, dieta, etc.). El editor luego permite
 * ajustar todo libremente.
 *
 * Decisiones:
 * - `applyDefaults` es PURO: clona el plan recibido y devuelve uno nuevo. No
 *   muta entradas. Esto facilita testing y evita side-effects en el editor.
 * - Si el plan ya tiene `clientName` / `fullName` con valor (porque se vinculó
 *   un cliente antes de aplicar plantilla), se respeta. Solo se rellena cuando
 *   está vacío.
 * - Si el plan ya tiene brand con primary/accent (coach configuró colores
 *   propios), se respetan. La plantilla solo aporta primary/accent cuando el
 *   coach no los tiene seteados — esto evita pisar la marca personal.
 * - Las rutinas no incluyen `gifUrl`: el coach asigna GIFs reales después
 *   desde el ExercisePicker (catálogo backend).
 *
 * Para agregar una nueva plantilla: añadir entrada en `TEMPLATES` con su
 * `applyDefaults` (puedes reutilizar `buildBaseDefaults` para el shell).
 */
import type {
  CardioData,
  CardioSession,
  ClientData,
  CoachBioData,
  CoverData,
  DietData,
  DietDay,
  DietMeal,
  NotesData,
  PlanDoc,
  PlanSection,
  RoutineData,
  RoutineDay,
  RoutineExercise,
  RoutineWeek,
  SupplementsData,
} from "./plan";

// ----------------------------------------------------------------------
// Tipos públicos
// ----------------------------------------------------------------------

export type TemplateCategory =
  | "Hipertrofia"
  | "Definición"
  | "Powerlifting"
  | "Recomp"
  | "Calistenia"
  | "Mujer Fitness"
  | "Endurance"
  | "Iniciación";

export interface TemplatePalette {
  primary: string;
  accent: string;
  ink: string;
  bg: string;
}

export interface TemplateTypography {
  display: string;
  body: string;
}

/**
 * Estilo de capa que se compone sobre la imagen para asegurar contraste y
 * personalidad visual.
 */
export type TemplateCoverOverlay = "dark-gradient" | "color-tint" | "split" | "duotone" | "none";

/**
 * Layout estructural de la portada — define cómo se arman imagen, color y
 * texto. Cada layout tiene su propio componente en `<TemplateCover>`.
 */
export type TemplateCoverLayout =
  | "hero-bottom"
  | "hero-top"
  | "split-vertical"
  | "split-diagonal"
  | "frame-border"
  | "magazine"
  | "minimal-icon"
  | "polaroid";

export interface TemplateCover {
  /** URL de imagen Unsplash u otra fuente. Cada portada tiene fallback automático. */
  imageUrl: string;
  /** Posición de la imagen en el contenedor — útil para centrar foco. */
  imagePosition?: string;
  /** Estilo de capa sobre la imagen para garantizar contraste con el texto. */
  overlay: TemplateCoverOverlay;
  /** Composición visual de la portada. */
  layout: TemplateCoverLayout;
  /** SVG inline (sólo path d=) para layouts que muestran un icono — ej "minimal-icon". */
  iconSvg?: string;
  /** Tagline corto que aparece en preview (no en el plan real, sólo en la galería). */
  tagline?: string;
}

export interface PlanTemplate {
  id: string;
  label: string;
  category: TemplateCategory;
  description: string;
  palette: TemplatePalette;
  typography: TemplateTypography;
  /** Configuración de portada visual para galería + preview. Opcional hasta que C1-v2 termine. */
  cover?: TemplateCover;
  /** Aplica los defaults del template a un PlanDoc nuevo (puro, no muta). */
  applyDefaults: (plan: PlanDoc) => PlanDoc;
}

/** Categorías para los chips del filtro. "Todas" va primero por convención UX. */
export const TEMPLATE_CATEGORIES: ReadonlyArray<"Todas" | TemplateCategory> = [
  "Todas",
  "Hipertrofia",
  "Definición",
  "Powerlifting",
  "Recomp",
  "Calistenia",
  "Mujer Fitness",
  "Endurance",
  "Iniciación",
];

// ----------------------------------------------------------------------
// Helpers internos
// ----------------------------------------------------------------------

const DEFAULT_INK = "#0a0a0a";
const DEFAULT_BG = "#ffffff";

let __exCounter = 0;
function exId(): string {
  __exCounter += 1;
  return `exd_${__exCounter}_${Math.random().toString(36).slice(2, 6)}`;
}

function dayId(): string {
  return `dyd_${Math.random().toString(36).slice(2, 8)}`;
}

function weekId(): string {
  return `wkd_${Math.random().toString(36).slice(2, 8)}`;
}

interface ExSpec {
  name: string;
  sets?: number | string;
  reps?: string;
  rest?: string;
  rir?: number | string;
  notes?: string;
  muscle?: string;
  equipment?: string;
}

function ex(spec: ExSpec): RoutineExercise {
  return {
    id: exId(),
    name: spec.name,
    sets: spec.sets ?? 4,
    reps: spec.reps ?? "8-12",
    weight: "",
    rest: spec.rest ?? "90s",
    rir: spec.rir ?? 2,
    notes: spec.notes ?? "",
    muscle: spec.muscle,
    equipment: spec.equipment,
  };
}

interface DaySpec {
  label: string;
  focus: string;
  warmup?: string;
  exercises: RoutineExercise[];
}

function buildDay(spec: DaySpec): RoutineDay {
  return {
    id: dayId(),
    label: spec.label,
    focus: spec.focus,
    warmup: spec.warmup ?? "5-10 min · movilidad articular + activación específica",
    exercises: spec.exercises,
  };
}

function buildWeek(label: string, days: RoutineDay[]): RoutineWeek {
  return { id: weekId(), label, days };
}

function meal(name: string, foods: string, kcal: string, p: string, c: string, f: string): DietMeal {
  return { name, foods, kcal, p, c, f };
}

function dietDay(label: string, meals: DietMeal[]): DietDay {
  return { id: `dd_${Math.random().toString(36).slice(2, 8)}`, label, meals };
}

function cardioSession(
  day: string,
  type: string,
  duration: string,
  intensity: string,
  notes = "",
): CardioSession {
  return { day, type, duration, intensity, notes };
}

// ----------------------------------------------------------------------
// Helper genérico que arma defaults de UN template
// ----------------------------------------------------------------------

interface TemplateContent {
  cover: Partial<CoverData>;
  client: Partial<ClientData>;
  coachBio: Partial<CoachBioData>;
  routine: RoutineData;
  cardio: CardioData;
  diet: DietData;
  supplements: SupplementsData;
  notes: NotesData;
}

/**
 * Construye un `applyDefaults` a partir de la palette + content de un template.
 * Mezcla con el plan existente sin pisar campos que el coach ya rellenó
 * (clientName, fullName, brand.primary/accent).
 */
function makeApply(
  templateId: string,
  palette: TemplatePalette,
  content: TemplateContent,
  theme: "light" | "dark" = "light",
  coverVisual?: TemplateCover,
  typography?: TemplateTypography,
): (plan: PlanDoc) => PlanDoc {
  return (plan: PlanDoc): PlanDoc => {
    const next: PlanDoc = {
      ...plan,
      template: templateId,
      brand: {
        ...(plan.brand ?? {}),
        primary: plan.brand?.primary || palette.primary,
        accent: plan.brand?.accent || palette.accent,
        // La tipografía de la plantilla se aplica al plan (igual que en la
        // galería) salvo que el coach ya haya elegido fuentes propias.
        displayFont: plan.brand?.displayFont || typography?.display,
        bodyFont: plan.brand?.bodyFont || typography?.body,
        // El tema lo decide la plantilla — las "dark editorial" fuerzan dark
        // para que las secciones del plan se rendericen con fondo oscuro.
        // Una plantilla light nunca pisa un theme dark ya elegido por el coach.
        theme: theme === "dark" ? "dark" : plan.brand?.theme,
      },
      sections: plan.sections.map((sec): PlanSection => {
        switch (sec.type) {
          case "cover": {
            const cur = sec.data;
            return {
              ...sec,
              data: {
                eyebrow: content.cover.eyebrow ?? cur.eyebrow,
                programTitle: cur.programTitle || content.cover.programTitle || "",
                programSubtitle: cur.programSubtitle || content.cover.programSubtitle || "",
                clientName: cur.clientName || content.cover.clientName || "",
                month: cur.month || content.cover.month || "",
                duration: cur.duration || content.cover.duration || "",
                // Portada visual: la imagen + overlay de la plantilla se aplican
                // al plan real para que se vea como en la galería (no solo color).
                imageUrl: cur.imageUrl || coverVisual?.imageUrl,
                imagePosition: cur.imagePosition || coverVisual?.imagePosition,
                overlay: cur.overlay || coverVisual?.overlay,
              },
            };
          }
          case "client": {
            const cur = sec.data;
            return {
              ...sec,
              data: {
                ...cur,
                fullName: cur.fullName || content.client.fullName || "",
                goal: cur.goal || content.client.goal || "",
                level: cur.level || content.client.level || "",
              },
            };
          }
          case "coachBio": {
            const cur = sec.data;
            return {
              ...sec,
              data: {
                bio: cur.bio || content.coachBio.bio || "",
                methodology: cur.methodology || content.coachBio.methodology || "",
                stats: cur.stats.length > 0 ? cur.stats : content.coachBio.stats ?? [],
                photo: cur.photo,
                contact: cur.contact || content.coachBio.contact || "",
              },
            };
          }
          case "routine":
            return { ...sec, data: content.routine };
          case "cardio":
            return { ...sec, data: content.cardio };
          case "diet":
            return { ...sec, data: content.diet };
          case "supplements":
            return { ...sec, data: content.supplements };
          case "notes":
            return { ...sec, data: content.notes };
          // `measurements` (y cualquier sección futura) no se personaliza por
          // plantilla: se deja con el `data` por defecto que generó defaultPlan.
          // El `default` evita que una sección sin caso devuelva `undefined` y
          // deje un hueco en el array (causa de la pantalla en blanco al abrir
          // el preview de una plantilla).
          case "measurements":
          default:
            return sec;
        }
      }),
      updatedAt: Date.now(),
    };
    return next;
  };
}

// ----------------------------------------------------------------------
// Contenidos por categoría (mapas reutilizables — las plantillas comparten
// estructura base y luego personalizan títulos/macros/notas)
// ----------------------------------------------------------------------

// --- Hipertrofia (volumen alto, 5 días, push/pull/legs+upper/lower) ---

function hyperRoutine(label: string): RoutineData {
  return {
    weeks: [
      buildWeek(label, [
        buildDay({
          label: "Lunes",
          focus: "Push (pecho · hombro · tríceps)",
          exercises: [
            ex({ name: "Press banca con barra", sets: 4, reps: "6-10", rest: "120s", muscle: "Pecho" }),
            ex({ name: "Press inclinado mancuernas", sets: 4, reps: "8-12", rest: "90s", muscle: "Pecho" }),
            ex({ name: "Press militar sentado", sets: 4, reps: "8-12", rest: "90s", muscle: "Hombro" }),
            ex({ name: "Aperturas en polea", sets: 3, reps: "12-15", rest: "60s", muscle: "Pecho" }),
            ex({ name: "Elevaciones laterales", sets: 4, reps: "12-15", rest: "60s", muscle: "Hombro" }),
            ex({ name: "Extensiones tríceps en polea", sets: 3, reps: "12-15", rest: "60s", muscle: "Tríceps" }),
          ],
        }),
        buildDay({
          label: "Martes",
          focus: "Pull (espalda · bíceps)",
          exercises: [
            ex({ name: "Dominadas o jalón al pecho", sets: 4, reps: "8-12", rest: "120s", muscle: "Espalda" }),
            ex({ name: "Remo con barra", sets: 4, reps: "8-10", rest: "120s", muscle: "Espalda" }),
            ex({ name: "Remo en máquina", sets: 3, reps: "10-12", rest: "90s", muscle: "Espalda" }),
            ex({ name: "Pullover en polea", sets: 3, reps: "12-15", rest: "60s", muscle: "Espalda" }),
            ex({ name: "Curl con barra", sets: 4, reps: "10-12", rest: "60s", muscle: "Bíceps" }),
            ex({ name: "Curl martillo", sets: 3, reps: "12-15", rest: "60s", muscle: "Bíceps" }),
          ],
        }),
        buildDay({
          label: "Miércoles",
          focus: "Legs (cuádriceps · glúteo)",
          exercises: [
            ex({ name: "Sentadilla con barra", sets: 4, reps: "6-10", rest: "150s", muscle: "Cuádriceps" }),
            ex({ name: "Prensa 45°", sets: 4, reps: "10-12", rest: "120s", muscle: "Cuádriceps" }),
            ex({ name: "Hip thrust con barra", sets: 4, reps: "10-12", rest: "120s", muscle: "Glúteo" }),
            ex({ name: "Extensiones de cuádriceps", sets: 3, reps: "12-15", rest: "60s", muscle: "Cuádriceps" }),
            ex({ name: "Elevación de talones", sets: 4, reps: "15-20", rest: "60s", muscle: "Pantorrilla" }),
          ],
        }),
        buildDay({ label: "Jueves", focus: "Descanso o cardio ligero", exercises: [] }),
        buildDay({
          label: "Viernes",
          focus: "Upper (full body parte superior)",
          exercises: [
            ex({ name: "Press inclinado con barra", sets: 4, reps: "8-10", rest: "120s", muscle: "Pecho" }),
            ex({ name: "Remo con mancuerna", sets: 4, reps: "10-12", rest: "90s", muscle: "Espalda" }),
            ex({ name: "Press Arnold", sets: 3, reps: "10-12", rest: "90s", muscle: "Hombro" }),
            ex({ name: "Face pull", sets: 3, reps: "15-20", rest: "60s", muscle: "Hombro" }),
            ex({ name: "Curl alterno", sets: 3, reps: "12-15", rest: "60s", muscle: "Bíceps" }),
            ex({ name: "Press francés", sets: 3, reps: "10-12", rest: "60s", muscle: "Tríceps" }),
          ],
        }),
        buildDay({
          label: "Sábado",
          focus: "Lower (femoral · glúteo)",
          exercises: [
            ex({ name: "Peso muerto rumano", sets: 4, reps: "8-10", rest: "120s", muscle: "Femoral" }),
            ex({ name: "Sentadilla búlgara", sets: 3, reps: "10-12", rest: "90s", muscle: "Cuádriceps" }),
            ex({ name: "Curl femoral acostado", sets: 4, reps: "12-15", rest: "60s", muscle: "Femoral" }),
            ex({ name: "Abducción en máquina", sets: 4, reps: "15-20", rest: "60s", muscle: "Glúteo" }),
            ex({ name: "Plancha frontal", sets: 3, reps: "45s", rest: "45s", muscle: "Core" }),
          ],
        }),
        buildDay({ label: "Domingo", focus: "Descanso", exercises: [] }),
      ]),
    ],
  };
}

// --- Definición / Cutting (déficit, 5 días + cardio agresivo) ---

function cutRoutine(label: string): RoutineData {
  return {
    weeks: [
      buildWeek(label, [
        buildDay({
          label: "Lunes",
          focus: "Pecho · hombro · tríceps + HIIT",
          exercises: [
            ex({ name: "Press inclinado mancuernas", sets: 4, reps: "10-12", rest: "75s", muscle: "Pecho" }),
            ex({ name: "Press militar máquina", sets: 4, reps: "10-12", rest: "75s", muscle: "Hombro" }),
            ex({ name: "Aperturas inclinadas", sets: 3, reps: "12-15", rest: "60s", muscle: "Pecho" }),
            ex({ name: "Elevaciones laterales", sets: 4, reps: "12-15", rest: "45s", muscle: "Hombro" }),
            ex({ name: "Fondos en paralelas", sets: 3, reps: "AMRAP", rest: "60s", muscle: "Tríceps" }),
            ex({ name: "Extensión tríceps polea", sets: 3, reps: "12-15", rest: "45s", muscle: "Tríceps" }),
          ],
        }),
        buildDay({
          label: "Martes",
          focus: "Espalda · bíceps + cardio",
          exercises: [
            ex({ name: "Jalón al pecho agarre ancho", sets: 4, reps: "10-12", rest: "75s", muscle: "Espalda" }),
            ex({ name: "Remo en máquina", sets: 4, reps: "10-12", rest: "75s", muscle: "Espalda" }),
            ex({ name: "Remo en polea baja", sets: 3, reps: "12-15", rest: "60s", muscle: "Espalda" }),
            ex({ name: "Curl con barra", sets: 3, reps: "12-15", rest: "45s", muscle: "Bíceps" }),
            ex({ name: "Curl martillo", sets: 3, reps: "12-15", rest: "45s", muscle: "Bíceps" }),
          ],
        }),
        buildDay({
          label: "Miércoles",
          focus: "Pierna completa + abs",
          exercises: [
            ex({ name: "Sentadilla goblet", sets: 4, reps: "10-12", rest: "90s", muscle: "Cuádriceps" }),
            ex({ name: "Prensa unilateral", sets: 3, reps: "12-15", rest: "75s", muscle: "Cuádriceps" }),
            ex({ name: "Peso muerto rumano", sets: 4, reps: "10-12", rest: "90s", muscle: "Femoral" }),
            ex({ name: "Hip thrust", sets: 4, reps: "12-15", rest: "75s", muscle: "Glúteo" }),
            ex({ name: "Crunch en máquina", sets: 4, reps: "15-20", rest: "45s", muscle: "Core" }),
          ],
        }),
        buildDay({ label: "Jueves", focus: "Cardio LISS · 45 min", exercises: [] }),
        buildDay({
          label: "Viernes",
          focus: "Full body intensidad",
          exercises: [
            ex({ name: "Thrusters mancuernas", sets: 4, reps: "10-12", rest: "60s" }),
            ex({ name: "Remo renegado", sets: 3, reps: "10-12", rest: "60s", muscle: "Espalda" }),
            ex({ name: "Burpees", sets: 4, reps: "15", rest: "45s" }),
            ex({ name: "Mountain climbers", sets: 4, reps: "30s", rest: "30s", muscle: "Core" }),
            ex({ name: "Plancha lateral", sets: 3, reps: "30s/lado", rest: "30s", muscle: "Core" }),
          ],
        }),
        buildDay({ label: "Sábado", focus: "Cardio + abs", exercises: [
          ex({ name: "Crunch abdominal", sets: 4, reps: "20", rest: "45s", muscle: "Core" }),
          ex({ name: "Russian twist", sets: 4, reps: "20/lado", rest: "45s", muscle: "Core" }),
          ex({ name: "Hollow hold", sets: 3, reps: "30s", rest: "45s", muscle: "Core" }),
        ] }),
        buildDay({ label: "Domingo", focus: "Descanso activo · caminata", exercises: [] }),
      ]),
    ],
  };
}

// --- Powerlifting (3 días, foco en SBD + accesorios) ---

function powerRoutine(label: string): RoutineData {
  return {
    weeks: [
      buildWeek(label, [
        buildDay({
          label: "Lunes",
          focus: "Squat · Día de sentadilla",
          warmup: "10 min · ramp-up con barra vacía + sets de aproximación",
          exercises: [
            ex({ name: "Sentadilla con barra (back squat)", sets: 5, reps: "3-5", rest: "180s", rir: 1, muscle: "Cuádriceps" }),
            ex({ name: "Pausa sentadilla 2 seg", sets: 3, reps: "5", rest: "150s", rir: 2, muscle: "Cuádriceps" }),
            ex({ name: "Prensa pesada", sets: 3, reps: "8", rest: "120s", muscle: "Cuádriceps" }),
            ex({ name: "Curl femoral acostado", sets: 3, reps: "10-12", rest: "75s", muscle: "Femoral" }),
            ex({ name: "Plancha lastrada", sets: 3, reps: "45s", rest: "60s", muscle: "Core" }),
          ],
        }),
        buildDay({ label: "Martes", focus: "Descanso", exercises: [] }),
        buildDay({
          label: "Miércoles",
          focus: "Bench · Día de press banca",
          warmup: "10 min · banda + ramp-up",
          exercises: [
            ex({ name: "Press banca con barra", sets: 5, reps: "3-5", rest: "180s", rir: 1, muscle: "Pecho" }),
            ex({ name: "Press cerrado pausado", sets: 3, reps: "5", rest: "150s", rir: 2, muscle: "Tríceps" }),
            ex({ name: "Press inclinado mancuernas", sets: 3, reps: "8-10", rest: "120s", muscle: "Pecho" }),
            ex({ name: "Remo con barra", sets: 4, reps: "6-8", rest: "120s", muscle: "Espalda" }),
            ex({ name: "Face pull", sets: 3, reps: "15", rest: "60s", muscle: "Hombro" }),
          ],
        }),
        buildDay({ label: "Jueves", focus: "Descanso", exercises: [] }),
        buildDay({
          label: "Viernes",
          focus: "Deadlift · Día de peso muerto",
          warmup: "10 min · activación posterior + ramp-up con barra",
          exercises: [
            ex({ name: "Peso muerto convencional", sets: 5, reps: "1-3", rest: "210s", rir: 1, muscle: "Espalda" }),
            ex({ name: "Peso muerto déficit", sets: 3, reps: "5", rest: "180s", rir: 2, muscle: "Femoral" }),
            ex({ name: "Buenos días con barra", sets: 3, reps: "8", rest: "120s", muscle: "Femoral" }),
            ex({ name: "Dominadas lastradas", sets: 4, reps: "5-8", rest: "120s", muscle: "Espalda" }),
            ex({ name: "Encogimientos con barra", sets: 3, reps: "10-12", rest: "75s", muscle: "Trapecio" }),
          ],
        }),
        buildDay({ label: "Sábado", focus: "Accesorios opcionales / movilidad", exercises: [] }),
        buildDay({ label: "Domingo", focus: "Descanso", exercises: [] }),
      ]),
    ],
  };
}

// --- Recomp (4 días balance fuerza + hipertrofia + algo cardio) ---

function recompRoutine(label: string): RoutineData {
  return {
    weeks: [
      buildWeek(label, [
        buildDay({
          label: "Lunes",
          focus: "Upper fuerza",
          exercises: [
            ex({ name: "Press banca con barra", sets: 4, reps: "5-8", rest: "120s", muscle: "Pecho" }),
            ex({ name: "Remo con barra", sets: 4, reps: "6-8", rest: "120s", muscle: "Espalda" }),
            ex({ name: "Press militar mancuernas", sets: 3, reps: "8-10", rest: "90s", muscle: "Hombro" }),
            ex({ name: "Dominadas", sets: 3, reps: "AMRAP", rest: "90s", muscle: "Espalda" }),
            ex({ name: "Curl + extensión superset", sets: 3, reps: "10/10", rest: "75s" }),
          ],
        }),
        buildDay({
          label: "Martes",
          focus: "Lower fuerza",
          exercises: [
            ex({ name: "Sentadilla con barra", sets: 4, reps: "5-8", rest: "150s", muscle: "Cuádriceps" }),
            ex({ name: "Peso muerto rumano", sets: 4, reps: "8-10", rest: "120s", muscle: "Femoral" }),
            ex({ name: "Sentadilla búlgara", sets: 3, reps: "10-12", rest: "90s", muscle: "Cuádriceps" }),
            ex({ name: "Hip thrust", sets: 3, reps: "10-12", rest: "90s", muscle: "Glúteo" }),
            ex({ name: "Plancha + ab wheel superset", sets: 3, reps: "45s/8", rest: "60s", muscle: "Core" }),
          ],
        }),
        buildDay({ label: "Miércoles", focus: "Cardio LISS · 30-40 min", exercises: [] }),
        buildDay({
          label: "Jueves",
          focus: "Upper hipertrofia",
          exercises: [
            ex({ name: "Press inclinado mancuernas", sets: 4, reps: "10-12", rest: "75s", muscle: "Pecho" }),
            ex({ name: "Jalón al pecho", sets: 4, reps: "10-12", rest: "75s", muscle: "Espalda" }),
            ex({ name: "Aperturas en polea", sets: 3, reps: "12-15", rest: "60s", muscle: "Pecho" }),
            ex({ name: "Remo en polea baja", sets: 3, reps: "12-15", rest: "60s", muscle: "Espalda" }),
            ex({ name: "Elevaciones laterales", sets: 4, reps: "15", rest: "45s", muscle: "Hombro" }),
          ],
        }),
        buildDay({
          label: "Viernes",
          focus: "Lower hipertrofia + glúteo",
          exercises: [
            ex({ name: "Prensa 45°", sets: 4, reps: "10-12", rest: "90s", muscle: "Cuádriceps" }),
            ex({ name: "Hip thrust pesado", sets: 4, reps: "8-10", rest: "120s", muscle: "Glúteo" }),
            ex({ name: "Curl femoral sentado", sets: 4, reps: "12-15", rest: "60s", muscle: "Femoral" }),
            ex({ name: "Abducción de cadera máquina", sets: 4, reps: "15-20", rest: "45s", muscle: "Glúteo" }),
            ex({ name: "Elevación de talones", sets: 4, reps: "15-20", rest: "45s", muscle: "Pantorrilla" }),
          ],
        }),
        buildDay({ label: "Sábado", focus: "Cardio o descanso activo", exercises: [] }),
        buildDay({ label: "Domingo", focus: "Descanso", exercises: [] }),
      ]),
    ],
  };
}

// --- Calistenia (5 días, peso corporal + skills) ---

function calisRoutine(label: string): RoutineData {
  return {
    weeks: [
      buildWeek(label, [
        buildDay({
          label: "Lunes",
          focus: "Push (pecho · tríceps · hombro)",
          exercises: [
            ex({ name: "Flexiones diamante", sets: 4, reps: "12-15", rest: "60s", muscle: "Tríceps" }),
            ex({ name: "Fondos en paralelas", sets: 4, reps: "8-12", rest: "90s", muscle: "Pecho" }),
            ex({ name: "Pseudo planche pushups", sets: 4, reps: "8-10", rest: "75s", muscle: "Hombro" }),
            ex({ name: "Pike pushups", sets: 3, reps: "10-12", rest: "60s", muscle: "Hombro" }),
            ex({ name: "Plancha hueca (hollow)", sets: 3, reps: "30-45s", rest: "45s", muscle: "Core" }),
          ],
        }),
        buildDay({
          label: "Martes",
          focus: "Pull (espalda · bíceps)",
          exercises: [
            ex({ name: "Dominadas pronas", sets: 5, reps: "5-8", rest: "120s", muscle: "Espalda" }),
            ex({ name: "Dominadas supinas (chin-ups)", sets: 4, reps: "6-10", rest: "90s", muscle: "Bíceps" }),
            ex({ name: "Remo australiano", sets: 4, reps: "10-12", rest: "75s", muscle: "Espalda" }),
            ex({ name: "Scapular pulls", sets: 3, reps: "10-12", rest: "60s", muscle: "Espalda" }),
            ex({ name: "Hollow body hold", sets: 3, reps: "30s", rest: "45s", muscle: "Core" }),
          ],
        }),
        buildDay({
          label: "Miércoles",
          focus: "Pierna + core",
          exercises: [
            ex({ name: "Sentadilla pistol asistida", sets: 4, reps: "6-8/lado", rest: "90s", muscle: "Cuádriceps" }),
            ex({ name: "Sentadilla búlgara peso corporal", sets: 4, reps: "12-15", rest: "75s", muscle: "Cuádriceps" }),
            ex({ name: "Peso muerto a una pierna", sets: 3, reps: "10/lado", rest: "75s", muscle: "Femoral" }),
            ex({ name: "Saltos en cajón", sets: 4, reps: "8", rest: "90s" }),
            ex({ name: "L-sit", sets: 4, reps: "20-30s", rest: "60s", muscle: "Core" }),
          ],
        }),
        buildDay({ label: "Jueves", focus: "Skill day · técnica", exercises: [] }),
        buildDay({
          label: "Viernes",
          focus: "Full body explosivo + cardio",
          exercises: [
            ex({ name: "Burpees", sets: 5, reps: "12", rest: "60s" }),
            ex({ name: "Muscle-up negativos", sets: 4, reps: "3-5", rest: "120s", muscle: "Espalda" }),
            ex({ name: "Mountain climbers", sets: 4, reps: "30s", rest: "30s", muscle: "Core" }),
            ex({ name: "Sprints 20m", sets: 6, reps: "1", rest: "90s" }),
            ex({ name: "Plancha lateral", sets: 3, reps: "30s/lado", rest: "45s", muscle: "Core" }),
          ],
        }),
        buildDay({ label: "Sábado", focus: "Movilidad + handstand practice", exercises: [] }),
        buildDay({ label: "Domingo", focus: "Descanso", exercises: [] }),
      ]),
    ],
  };
}

// --- Mujer Fitness (énfasis glúteo · 5 días) ---

function femmeRoutine(label: string): RoutineData {
  return {
    weeks: [
      buildWeek(label, [
        buildDay({
          label: "Lunes",
          focus: "Glúteo + femoral",
          exercises: [
            ex({ name: "Hip thrust con barra", sets: 5, reps: "8-10", rest: "120s", muscle: "Glúteo" }),
            ex({ name: "Peso muerto rumano", sets: 4, reps: "10-12", rest: "90s", muscle: "Femoral" }),
            ex({ name: "Sentadilla búlgara", sets: 4, reps: "10-12", rest: "75s", muscle: "Glúteo" }),
            ex({ name: "Patada de glúteo en polea", sets: 4, reps: "12-15", rest: "60s", muscle: "Glúteo" }),
            ex({ name: "Abducción en máquina", sets: 4, reps: "15-20", rest: "45s", muscle: "Glúteo" }),
          ],
        }),
        buildDay({
          label: "Martes",
          focus: "Upper (pecho · espalda · brazo)",
          exercises: [
            ex({ name: "Press inclinado mancuernas", sets: 4, reps: "10-12", rest: "75s", muscle: "Pecho" }),
            ex({ name: "Jalón al pecho", sets: 4, reps: "10-12", rest: "75s", muscle: "Espalda" }),
            ex({ name: "Press militar mancuernas", sets: 3, reps: "10-12", rest: "75s", muscle: "Hombro" }),
            ex({ name: "Curl alterno", sets: 3, reps: "12", rest: "60s", muscle: "Bíceps" }),
            ex({ name: "Patada tríceps mancuerna", sets: 3, reps: "12-15", rest: "60s", muscle: "Tríceps" }),
          ],
        }),
        buildDay({
          label: "Miércoles",
          focus: "Cuádriceps + glúteo",
          exercises: [
            ex({ name: "Sentadilla goblet", sets: 4, reps: "10-12", rest: "90s", muscle: "Cuádriceps" }),
            ex({ name: "Prensa 45°", sets: 4, reps: "12-15", rest: "75s", muscle: "Cuádriceps" }),
            ex({ name: "Zancadas con mancuernas", sets: 3, reps: "12/lado", rest: "75s", muscle: "Cuádriceps" }),
            ex({ name: "Step up en banco", sets: 3, reps: "10/lado", rest: "60s", muscle: "Glúteo" }),
            ex({ name: "Extensiones de cuádriceps", sets: 3, reps: "15", rest: "45s", muscle: "Cuádriceps" }),
          ],
        }),
        buildDay({ label: "Jueves", focus: "Cardio LISS + abs", exercises: [
          ex({ name: "Crunch en suelo", sets: 4, reps: "20", rest: "45s", muscle: "Core" }),
          ex({ name: "Plancha frontal", sets: 4, reps: "45s", rest: "45s", muscle: "Core" }),
          ex({ name: "Bicicleta abdominal", sets: 3, reps: "20/lado", rest: "45s", muscle: "Core" }),
        ] }),
        buildDay({
          label: "Viernes",
          focus: "Glúteo bombing + cardio corto",
          exercises: [
            ex({ name: "Hip thrust unilateral", sets: 4, reps: "12/lado", rest: "75s", muscle: "Glúteo" }),
            ex({ name: "Sentadilla sumo", sets: 4, reps: "12-15", rest: "75s", muscle: "Glúteo" }),
            ex({ name: "Patada de glúteo banda", sets: 4, reps: "15", rest: "45s", muscle: "Glúteo" }),
            ex({ name: "Curl femoral acostado", sets: 3, reps: "12-15", rest: "60s", muscle: "Femoral" }),
            ex({ name: "Cardio HIIT 10 min", sets: 1, reps: "10 min", rest: "—" }),
          ],
        }),
        buildDay({ label: "Sábado", focus: "Caminata + movilidad", exercises: [] }),
        buildDay({ label: "Domingo", focus: "Descanso", exercises: [] }),
      ]),
    ],
  };
}

// --- Endurance (4 días, foco aeróbico + fuerza funcional) ---

function endurRoutine(label: string): RoutineData {
  return {
    weeks: [
      buildWeek(label, [
        buildDay({
          label: "Lunes",
          focus: "Long run · base aeróbica",
          warmup: "10 min · trote suave + drills de carrera",
          exercises: [
            ex({ name: "Carrera continua Z2", sets: 1, reps: "60-75 min", rest: "—" }),
            ex({ name: "Movilidad de cadera", sets: 3, reps: "10/lado", rest: "—" }),
          ],
        }),
        buildDay({
          label: "Martes",
          focus: "Fuerza funcional",
          exercises: [
            ex({ name: "Sentadilla goblet", sets: 4, reps: "10-12", rest: "75s", muscle: "Cuádriceps" }),
            ex({ name: "Peso muerto rumano", sets: 4, reps: "10", rest: "90s", muscle: "Femoral" }),
            ex({ name: "Press militar mancuernas", sets: 3, reps: "10-12", rest: "75s", muscle: "Hombro" }),
            ex({ name: "Remo en polea", sets: 3, reps: "10-12", rest: "75s", muscle: "Espalda" }),
            ex({ name: "Plancha frontal", sets: 3, reps: "45s", rest: "45s", muscle: "Core" }),
          ],
        }),
        buildDay({
          label: "Miércoles",
          focus: "Intervalos · VO2 max",
          warmup: "15 min · trote progresivo",
          exercises: [
            ex({ name: "Series 1000m", sets: 5, reps: "1000m", rest: "3 min", notes: "Ritmo 5K" }),
            ex({ name: "Estiramiento gemelos · sóleo", sets: 3, reps: "30s/lado", rest: "—" }),
          ],
        }),
        buildDay({ label: "Jueves", focus: "Recuperación · trote suave 30 min", exercises: [] }),
        buildDay({
          label: "Viernes",
          focus: "Tempo run + core",
          exercises: [
            ex({ name: "Carrera ritmo umbral", sets: 1, reps: "30 min", rest: "—", notes: "RPE 7-8" }),
            ex({ name: "Hollow hold", sets: 4, reps: "30s", rest: "30s", muscle: "Core" }),
            ex({ name: "Plancha lateral", sets: 3, reps: "30s/lado", rest: "30s", muscle: "Core" }),
          ],
        }),
        buildDay({ label: "Sábado", focus: "Cross training (bici / natación) 60 min", exercises: [] }),
        buildDay({ label: "Domingo", focus: "Descanso o caminata 30 min", exercises: [] }),
      ]),
    ],
  };
}

// --- Iniciación (3 días suaves, full body) ---

function beginRoutine(label: string): RoutineData {
  return {
    weeks: [
      buildWeek(label, [
        buildDay({
          label: "Lunes",
          focus: "Full body A — empuje y pierna",
          warmup: "5 min · marcha + movilidad articular",
          exercises: [
            ex({ name: "Sentadilla goblet ligera", sets: 3, reps: "10-12", rest: "75s", rir: 3, muscle: "Cuádriceps" }),
            ex({ name: "Press inclinado en máquina", sets: 3, reps: "10-12", rest: "75s", rir: 3, muscle: "Pecho" }),
            ex({ name: "Press militar máquina", sets: 3, reps: "10-12", rest: "75s", rir: 3, muscle: "Hombro" }),
            ex({ name: "Plancha de rodillas", sets: 3, reps: "20-30s", rest: "45s", muscle: "Core" }),
          ],
        }),
        buildDay({ label: "Martes", focus: "Descanso o caminata 20 min", exercises: [] }),
        buildDay({
          label: "Miércoles",
          focus: "Full body B — tracción y posterior",
          warmup: "5 min · movilidad + bandas",
          exercises: [
            ex({ name: "Jalón al pecho", sets: 3, reps: "10-12", rest: "75s", rir: 3, muscle: "Espalda" }),
            ex({ name: "Remo en máquina", sets: 3, reps: "10-12", rest: "75s", rir: 3, muscle: "Espalda" }),
            ex({ name: "Hip thrust en máquina", sets: 3, reps: "12-15", rest: "75s", rir: 3, muscle: "Glúteo" }),
            ex({ name: "Curl femoral acostado", sets: 3, reps: "12", rest: "60s", muscle: "Femoral" }),
            ex({ name: "Caminata 10 min cinta", sets: 1, reps: "10 min", rest: "—" }),
          ],
        }),
        buildDay({ label: "Jueves", focus: "Descanso", exercises: [] }),
        buildDay({
          label: "Viernes",
          focus: "Full body C — mixto",
          warmup: "5 min · movilidad",
          exercises: [
            ex({ name: "Prensa 45° ligera", sets: 3, reps: "12-15", rest: "75s", rir: 3, muscle: "Cuádriceps" }),
            ex({ name: "Aperturas en máquina (peck deck)", sets: 3, reps: "12-15", rest: "60s", muscle: "Pecho" }),
            ex({ name: "Curl con mancuerna", sets: 3, reps: "12", rest: "60s", muscle: "Bíceps" }),
            ex({ name: "Patada tríceps polea", sets: 3, reps: "12-15", rest: "60s", muscle: "Tríceps" }),
            ex({ name: "Caminata 15 min", sets: 1, reps: "15 min", rest: "—" }),
          ],
        }),
        buildDay({ label: "Sábado", focus: "Caminata + estiramientos", exercises: [] }),
        buildDay({ label: "Domingo", focus: "Descanso", exercises: [] }),
      ]),
    ],
  };
}

// --- Home / minimal equipment (4 días, mancuernas + bandas + peso corporal) ---

function homeRoutine(label: string): RoutineData {
  return {
    weeks: [
      buildWeek(label, [
        buildDay({
          label: "Lunes",
          focus: "Tren superior — empuje y tracción en casa",
          warmup: "5 min · movilidad de hombro + activación con banda",
          exercises: [
            ex({ name: "Press piso con mancuernas", sets: 4, reps: "10-12", rest: "75s", muscle: "Pecho" }),
            ex({ name: "Remo con mancuerna a una mano", sets: 4, reps: "10-12", rest: "75s", muscle: "Espalda" }),
            ex({ name: "Press hombro de pie con mancuernas", sets: 3, reps: "10-12", rest: "60s", muscle: "Hombro" }),
            ex({ name: "Jalón con banda elástica", sets: 3, reps: "12-15", rest: "45s", muscle: "Espalda" }),
            ex({ name: "Curl + extensión superset con mancuernas", sets: 3, reps: "12/12", rest: "60s" }),
          ],
        }),
        buildDay({
          label: "Martes",
          focus: "Tren inferior — pierna y glúteo en casa",
          warmup: "5 min · sentadilla peso corporal + puente de glúteo",
          exercises: [
            ex({ name: "Sentadilla goblet con mancuerna", sets: 4, reps: "12-15", rest: "75s", muscle: "Cuádriceps" }),
            ex({ name: "Zancada caminando con mancuernas", sets: 3, reps: "12/lado", rest: "75s", muscle: "Cuádriceps" }),
            ex({ name: "Peso muerto rumano con mancuernas", sets: 4, reps: "10-12", rest: "75s", muscle: "Femoral" }),
            ex({ name: "Hip thrust en suelo con mancuerna", sets: 4, reps: "15", rest: "60s", muscle: "Glúteo" }),
            ex({ name: "Elevación de talones de pie", sets: 4, reps: "15-20", rest: "45s", muscle: "Pantorrilla" }),
          ],
        }),
        buildDay({ label: "Miércoles", focus: "Cardio en casa · 25-30 min (saltar cuerda o circuito)", exercises: [] }),
        buildDay({
          label: "Jueves",
          focus: "Full body metabólico en casa",
          exercises: [
            ex({ name: "Thrusters con mancuernas", sets: 4, reps: "12", rest: "60s" }),
            ex({ name: "Remo renegado", sets: 3, reps: "10/lado", rest: "60s", muscle: "Espalda" }),
            ex({ name: "Sentadilla con salto", sets: 4, reps: "12", rest: "45s", muscle: "Cuádriceps" }),
            ex({ name: "Flexiones", sets: 3, reps: "AMRAP", rest: "60s", muscle: "Pecho" }),
            ex({ name: "Plancha con toque de hombro", sets: 3, reps: "30s", rest: "45s", muscle: "Core" }),
          ],
        }),
        buildDay({
          label: "Viernes",
          focus: "Core + glúteo + movilidad",
          exercises: [
            ex({ name: "Patada de glúteo con banda", sets: 4, reps: "15/lado", rest: "45s", muscle: "Glúteo" }),
            ex({ name: "Crunch en suelo", sets: 4, reps: "20", rest: "45s", muscle: "Core" }),
            ex({ name: "Plancha lateral", sets: 3, reps: "30s/lado", rest: "45s", muscle: "Core" }),
            ex({ name: "Hollow hold", sets: 3, reps: "30s", rest: "45s", muscle: "Core" }),
          ],
        }),
        buildDay({ label: "Sábado", focus: "Caminata + estiramientos", exercises: [] }),
        buildDay({ label: "Domingo", focus: "Descanso", exercises: [] }),
      ]),
    ],
  };
}

// ----------------------------------------------------------------------
// Cardio · Diet · Supplements · Notes builders
// ----------------------------------------------------------------------

const CARDIO_HYPER: CardioData = {
  summary: "Cardio bajo impacto 2x/sem para sumar gasto sin canibalizar fuerza.",
  sessions: [
    cardioSession("Martes", "Caminata inclinada", "30 min", "Z2 · 60-65% FCM", "Post entreno"),
    cardioSession("Sábado", "Bicicleta estática", "40 min", "Z2 · 65% FCM", "Sesión separada"),
  ],
};

const CARDIO_CUT: CardioData = {
  summary: "Cardio agresivo 4x/sem combinando LISS y HIIT para maximizar déficit.",
  sessions: [
    cardioSession("Lunes", "HIIT bici", "20 min", "30s ON / 60s OFF x 8", ""),
    cardioSession("Miércoles", "Caminata inclinada", "45 min", "Z2 · 65% FCM", "En ayunas si toleras"),
    cardioSession("Jueves", "LISS cinta", "45 min", "Z2", ""),
    cardioSession("Sábado", "HIIT remo", "20 min", "20s ON / 40s OFF x 10", ""),
  ],
};

const CARDIO_POWER: CardioData = {
  summary: "Cardio mínimo, solo para salud cardiovascular sin restar recuperación.",
  sessions: [
    cardioSession("Sábado", "Caminata inclinada", "30 min", "Z2 · conversacional", "Lejos de squat/deadlift"),
    cardioSession("Domingo", "Bicicleta suave", "30 min", "Z1-Z2", "Recuperación activa"),
  ],
};

const CARDIO_RECOMP: CardioData = {
  summary: "2-3 sesiones/sem en zona aeróbica para mejorar capacidad y gasto.",
  sessions: [
    cardioSession("Miércoles", "LISS bici", "35 min", "Z2 · 65% FCM", ""),
    cardioSession("Sábado", "HIIT corto", "15 min", "30s/30s x 10", "Opcional según fatiga"),
  ],
};

const CARDIO_CALIS: CardioData = {
  summary: "Cardio explosivo integrado a la rutina + sprints semanales.",
  sessions: [
    cardioSession("Viernes", "Sprints", "20 min", "6 x 20m all-out", "Recuperación completa entre sprints"),
    cardioSession("Sábado", "Caminata + skill", "45 min", "Z2", ""),
  ],
};

const CARDIO_FEMME: CardioData = {
  summary: "Cardio LISS para ayudar a déficit sin canibalizar el progreso de glúteo.",
  sessions: [
    cardioSession("Jueves", "Caminata inclinada", "40 min", "Z2 · 65% FCM", "10k pasos al día como meta"),
    cardioSession("Sábado", "Bicicleta", "30 min", "Z2", ""),
  ],
};

const CARDIO_ENDUR: CardioData = {
  summary: "Foco principal · 4-5 sesiones aeróbicas con periodización tradicional.",
  sessions: [
    cardioSession("Lunes", "Long run", "60-75 min", "Z2 · conversacional", "Base"),
    cardioSession("Miércoles", "Intervalos VO2", "45 min", "5 x 1000m a ritmo 5K", "Calentamiento + vuelta a la calma"),
    cardioSession("Viernes", "Tempo run", "30 min", "Umbral · RPE 7-8", ""),
    cardioSession("Sábado", "Cross-training", "60 min", "Bici o natación · Z2", "Recuperación activa"),
  ],
};

const CARDIO_BEGIN: CardioData = {
  summary: "Caminatas suaves para sumar gasto y crear hábito sin abrumar.",
  sessions: [
    cardioSession("Martes", "Caminata", "20 min", "Conversacional", ""),
    cardioSession("Jueves", "Caminata", "25 min", "Conversacional", ""),
    cardioSession("Sábado", "Caminata + estiramientos", "30 min", "Suave", ""),
  ],
};

// --- Diets ---

function dietHyper(): DietData {
  return {
    macros: { kcal: "2900", protein: "180g", carbs: "360g", fat: "75g" },
    summary: "Superávit calórico moderado (+250-350 kcal). Prioridad: proteína 1.8-2.0 g/kg.",
    days: [
      dietDay("Día tipo", [
        meal("Desayuno", "5 claras + 2 huevos · 80g avena · 1 banana · café", "650", "45g", "85g", "12g"),
        meal("Media mañana", "Yogur griego 200g · 30g almendras · miel", "400", "20g", "30g", "22g"),
        meal("Almuerzo", "180g pollo · 250g arroz · ensalada · aceite oliva 10ml", "780", "55g", "100g", "15g"),
        meal("Pre entreno", "1 banana · 30g whey", "230", "30g", "30g", "2g"),
        meal("Post entreno", "150g salmón · 200g batata · brócoli", "550", "40g", "55g", "18g"),
        meal("Cena", "200g carne magra · 150g quinoa · vegetales", "560", "45g", "55g", "12g"),
      ]),
    ],
  };
}

function dietCut(): DietData {
  return {
    macros: { kcal: "2000", protein: "200g", carbs: "180g", fat: "60g" },
    summary: "Déficit moderado (-400 kcal). Proteína alta para preservar masa muscular.",
    days: [
      dietDay("Día tipo", [
        meal("Desayuno", "6 claras + 1 huevo · 50g avena · café", "380", "40g", "35g", "10g"),
        meal("Media mañana", "Yogur 0% 200g · 100g frutos rojos", "180", "20g", "20g", "2g"),
        meal("Almuerzo", "180g pechuga · 150g arroz integral · ensalada", "560", "55g", "60g", "10g"),
        meal("Merienda", "1 manzana · 30g whey", "200", "28g", "25g", "2g"),
        meal("Cena", "180g pescado blanco · 200g vegetales al vapor · 1 cdta aceite", "320", "42g", "20g", "12g"),
        meal("Antes dormir", "Caseína 30g · 1 cdta crema almendras", "200", "30g", "8g", "8g"),
      ]),
    ],
  };
}

function dietPower(): DietData {
  return {
    macros: { kcal: "3200", protein: "180g", carbs: "420g", fat: "85g" },
    summary: "Superávit calórico para soportar cargas pesadas. Carbos altos pre-entreno.",
    days: [
      dietDay("Día tipo", [
        meal("Desayuno", "3 huevos · 100g avena · 1 banana · 30g mantequilla maní", "780", "40g", "85g", "32g"),
        meal("Media mañana", "Sandwich pollo · fruta · leche", "500", "35g", "55g", "12g"),
        meal("Pre entreno (-90 min)", "300g arroz · 180g pollo · vegetales", "780", "50g", "120g", "8g"),
        meal("Post entreno", "Whey 40g · 1 banana · 50g avena", "500", "45g", "70g", "8g"),
        meal("Cena", "200g res · 250g batata · ensalada · aceite", "640", "50g", "70g", "20g"),
      ]),
    ],
  };
}

function dietRecomp(): DietData {
  return {
    macros: { kcal: "2400", protein: "180g", carbs: "260g", fat: "75g" },
    summary: "Calorías de mantenimiento. Recomp lento priorizando calidad alimentaria.",
    days: [
      dietDay("Día tipo", [
        meal("Desayoun", "3 huevos · 60g avena · café", "490", "30g", "55g", "18g"),
        meal("Media mañana", "30g whey · 1 manzana · 20g almendras", "350", "30g", "30g", "13g"),
        meal("Almuerzo", "160g pollo · 180g arroz · ensalada · aceite", "640", "50g", "75g", "12g"),
        meal("Merienda", "Yogur griego 200g · frutos rojos · granola 30g", "350", "25g", "40g", "10g"),
        meal("Cena", "180g pescado · 200g batata · vegetales", "490", "42g", "55g", "12g"),
      ]),
    ],
  };
}

function dietCalis(): DietData {
  return {
    macros: { kcal: "2600", protein: "170g", carbs: "320g", fat: "70g" },
    summary: "Calorías para soportar volumen alto. Carbos para skills explosivas.",
    days: [
      dietDay("Día tipo", [
        meal("Desayuno", "4 claras + 2 huevos · 80g avena · banana", "620", "40g", "75g", "15g"),
        meal("Pre entreno", "1 banana · café · 30g whey", "230", "28g", "30g", "2g"),
        meal("Almuerzo", "170g pollo · 200g arroz · vegetales", "660", "50g", "85g", "12g"),
        meal("Post entreno", "30g whey · 1 fruta", "180", "28g", "20g", "2g"),
        meal("Cena", "180g carne · 180g batata · ensalada", "550", "45g", "55g", "16g"),
      ]),
    ],
  };
}

function dietFemme(): DietData {
  return {
    macros: { kcal: "1900", protein: "140g", carbs: "180g", fat: "60g" },
    summary: "Déficit moderado para definición. Proteína alta para mantener masa magra.",
    days: [
      dietDay("Día tipo", [
        meal("Desayuno", "3 claras + 1 huevo · 40g avena · 1 fruta", "320", "25g", "45g", "8g"),
        meal("Media mañana", "Yogur griego 0% · 100g frutos rojos · 15g almendras", "230", "20g", "20g", "8g"),
        meal("Almuerzo", "150g pechuga · 120g arroz · ensalada · aceite oliva", "490", "45g", "55g", "10g"),
        meal("Merienda", "Hummus 50g · zanahoria · 30g whey", "260", "30g", "20g", "8g"),
        meal("Cena", "150g salmón · vegetales asados · 1 cdta aceite", "400", "40g", "15g", "20g"),
      ]),
    ],
  };
}

function dietEndur(): DietData {
  return {
    macros: { kcal: "2800", protein: "130g", carbs: "420g", fat: "70g" },
    summary: "Carbos altos para soportar volumen aeróbico. Recovery post-long run clave.",
    days: [
      dietDay("Día tipo", [
        meal("Desayuno pre-run", "60g avena · 1 banana · miel · café", "450", "10g", "85g", "5g"),
        meal("Post long run", "Smoothie: leche · 30g whey · 1 banana · miel", "380", "35g", "50g", "4g"),
        meal("Almuerzo", "150g pasta · 150g pollo · salsa tomate · vegetales", "650", "40g", "85g", "12g"),
        meal("Merienda", "2 tostadas integrales · mermelada · 30g queso", "320", "15g", "45g", "10g"),
        meal("Cena", "180g pescado · 200g arroz · vegetales", "560", "40g", "75g", "10g"),
        meal("Snack", "Yogur · granola · fruta", "300", "15g", "45g", "8g"),
      ]),
    ],
  };
}

function dietBegin(): DietData {
  return {
    macros: { kcal: "1800", protein: "110g", carbs: "200g", fat: "55g" },
    summary: "Mantenimiento o leve déficit. Foco en hábitos: 4 comidas, 2L de agua, proteína en cada plato.",
    days: [
      dietDay("Día tipo", [
        meal("Desayuno", "2 huevos · 1 tostada integral · 1 fruta · café", "350", "20g", "35g", "12g"),
        meal("Almuerzo", "120g proteína magra · 1 plato carbo · ensalada generosa", "550", "40g", "60g", "15g"),
        meal("Merienda", "Yogur · fruta · puñado de frutos secos", "280", "15g", "30g", "12g"),
        meal("Cena", "120g proteína · vegetales abundantes · 1 cdta aceite", "420", "35g", "25g", "16g"),
      ]),
    ],
  };
}

// --- Supplements ---

const SUPP_HYPER: SupplementsData = {
  summary: "Stack básico para volumen + creatina diaria.",
  items: [
    { name: "Whey Protein", dose: "30-40g", timing: "Post entreno o entre comidas", notes: "" },
    { name: "Creatina monohidrato", dose: "5g", timing: "Diario · cualquier momento", notes: "Sin fase de carga" },
    { name: "Omega 3", dose: "2g EPA+DHA", timing: "Con la comida principal", notes: "" },
  ],
};

const SUPP_CUT: SupplementsData = {
  summary: "Soporte para preservar masa magra en déficit.",
  items: [
    { name: "Whey Protein", dose: "30g", timing: "Post entreno o snack", notes: "Saciante en déficit" },
    { name: "Creatina monohidrato", dose: "5g", timing: "Diario", notes: "" },
    { name: "Cafeína", dose: "200mg", timing: "Pre entreno", notes: "Si toleras" },
    { name: "Multivitamínico", dose: "1 dosis", timing: "Con desayuno", notes: "" },
  ],
};

const SUPP_POWER: SupplementsData = {
  summary: "Foco en performance y articulaciones.",
  items: [
    { name: "Creatina monohidrato", dose: "5-10g", timing: "Diario", notes: "Clave para fuerza máxima" },
    { name: "Whey Protein", dose: "30-40g", timing: "Post entreno", notes: "" },
    { name: "Cafeína", dose: "300mg", timing: "Pre entreno pesado", notes: "Solo días de SBD" },
    { name: "Colágeno + Vit C", dose: "10g + 50mg", timing: "Pre entreno (-30 min)", notes: "Soporte tendones" },
  ],
};

const SUPP_RECOMP: SupplementsData = {
  summary: "Stack equilibrado para rendimiento sin exceso.",
  items: [
    { name: "Whey Protein", dose: "30g", timing: "Post entreno", notes: "" },
    { name: "Creatina monohidrato", dose: "5g", timing: "Diario", notes: "" },
    { name: "Omega 3", dose: "2g EPA+DHA", timing: "Comida principal", notes: "" },
  ],
};

const SUPP_CALIS: SupplementsData = {
  summary: "Mínimo necesario para soportar volumen + skills.",
  items: [
    { name: "Whey Protein", dose: "30g", timing: "Post entreno", notes: "" },
    { name: "Creatina monohidrato", dose: "5g", timing: "Diario", notes: "Mejora recuperación entre skills" },
    { name: "Magnesio", dose: "300mg", timing: "Antes de dormir", notes: "Recuperación neuromuscular" },
  ],
};

const SUPP_FEMME: SupplementsData = {
  summary: "Stack para definición + soporte hormonal y de piel.",
  items: [
    { name: "Whey Protein", dose: "25-30g", timing: "Post entreno o snack", notes: "Sabor a elección" },
    { name: "Creatina monohidrato", dose: "3-5g", timing: "Diario", notes: "También mejora composición femenina" },
    { name: "Colágeno hidrolizado", dose: "10g", timing: "Mañana o post entreno", notes: "Piel + articulaciones" },
    { name: "Vitamina D3", dose: "2000 UI", timing: "Con desayuno", notes: "" },
  ],
};

const SUPP_ENDUR: SupplementsData = {
  summary: "Soporte para volumen aeróbico alto · electrolitos clave.",
  items: [
    { name: "Carbohidratos en gel", dose: "30-60g/h", timing: "Long runs >75 min", notes: "" },
    { name: "Electrolitos (Na/K/Mg)", dose: "1 sobre", timing: "Pre y durante long run", notes: "Especialmente en calor" },
    { name: "Whey Protein", dose: "30g", timing: "Post sesión", notes: "" },
    { name: "Hierro (si déficit)", dose: "Según análisis", timing: "Lejos de café/lácteos", notes: "Solo con prescripción" },
  ],
};

const SUPP_BEGIN: SupplementsData = {
  summary: "Foco en alimentación primero · solo lo esencial al inicio.",
  items: [
    { name: "Whey Protein (opcional)", dose: "20-30g", timing: "Post entreno o snack", notes: "Solo si te cuesta llegar a la proteína diaria" },
    { name: "Multivitamínico", dose: "1 dosis", timing: "Con desayuno", notes: "Apoyo general" },
  ],
};

// --- Notes ---

function buildNotes(body: string, signature = "Tu coach"): NotesData {
  return { body, signature };
}

// ----------------------------------------------------------------------
// Definición de las 15 plantillas
// ----------------------------------------------------------------------

interface TemplateSpec {
  id: string;
  label: string;
  category: TemplateCategory;
  description: string;
  palette: TemplatePalette;
  typography: TemplateTypography;
  /** Texto eyebrow + métricas en cover, métodología en coach. */
  cover: { eyebrow: string; programTitle: string; programSubtitle: string; duration: string };
  /** Configuración visual de la portada (imagen + layout + overlay). Opcional hasta C1-v2 completo. */
  coverVisual?: TemplateCover;
  methodology: string;
  notes: string;
  /** Builder de la rutina (recibe label de semana). */
  routine: (label: string) => RoutineData;
  cardio: CardioData;
  diet: DietData;
  supplements: SupplementsData;
}

// ----------------------------------------------------------------------
// Helpers de portada visual — IDs Unsplash verificados.
// Si una imagen específica falla en runtime, el componente <TemplateCover>
// cae a un fallback temático con picsum.photos/seed/<id>.
// ----------------------------------------------------------------------

const UNSPLASH = (id: string, w = 1200) =>
  `https://images.unsplash.com/photo-${id}?w=${w}&auto=format&fit=crop&q=80`;

/**
 * SVG paths para los layouts "minimal-icon" — glifos fitness simplificados
 * que se renderizan grandes sobre fondo de color sólido. Solo el atributo `d`,
 * el componente arma el `<svg viewBox="0 0 24 24">` alrededor.
 */
const ICON_DUMBBELL =
  "M6.5 6.5h-2v11h2v-11zm15 1.5h-2v8h2v-8zm-3-1.5h-2v11h2v-11zm-12 4.5h10v2h-10v-2zm-3 1.5h-2v8h2v-8z";
const ICON_LIGHTNING =
  "M13 3l-9 12h6l-1 6 9-12h-6l1-6z";
const ICON_HEART =
  "M12 21s-7-4.5-9.5-9C0 8 2 4 6 4c2 0 3.5 1 3 1s4-1 6 1c4 4 0 9-3 11z";

const SPECS: TemplateSpec[] = [
  // 1
  {
    id: "hyper-classic",
    label: "Hipertrofia Clásico",
    category: "Hipertrofia",
    description: "Bloques de fuerza-hipertrofia con progresión semanal. 5 días split clásico.",
    palette: { primary: "#7c3aed", accent: "#ec4899", ink: DEFAULT_INK, bg: DEFAULT_BG },
    typography: { display: "Bricolage Grotesque", body: "Inter Tight" },
    cover: {
      eyebrow: "PROGRAMA DE HIPERTROFIA",
      programTitle: "Volumen y Fuerza",
      programSubtitle: "12 semanas de progresión periodizada",
      duration: "12 semanas",
    },
    methodology: "Periodización lineal con énfasis en volumen progresivo y técnica impecable.",
    notes: "Cumple los rangos de RIR. Si llegas a 0 antes del set final, baja el peso. Registra sets y reps cada sesión.",
    routine: hyperRoutine,
    cardio: CARDIO_HYPER,
    diet: dietHyper(),
    supplements: SUPP_HYPER,
  },
  // 2
  {
    id: "hyper-bold",
    label: "Hipertrofia Bold",
    category: "Hipertrofia",
    description: "Estética premium contraste alto. Para coaches con marca personal fuerte.",
    palette: { primary: "#ef4444", accent: "#1e293b", ink: DEFAULT_INK, bg: DEFAULT_BG },
    typography: { display: "Bricolage Grotesque", body: "Inter Tight" },
    cover: {
      eyebrow: "BOLD HYPERTROPHY",
      programTitle: "Más músculo · Más intensidad",
      programSubtitle: "8 semanas · Push Pull Legs amplificado",
      duration: "8 semanas",
    },
    methodology: "Alta intensidad con sets cercanos al fallo (RIR 0-1) en última serie.",
    notes: "Esta plantilla pide foco mental. Calienta bien, ejecuta lento la excéntrica y respira en cada rep.",
    routine: hyperRoutine,
    cardio: CARDIO_HYPER,
    diet: dietHyper(),
    supplements: SUPP_HYPER,
  },
  // 3
  {
    id: "hyper-editorial",
    label: "Hipertrofia Editorial",
    category: "Hipertrofia",
    description: "Vibra revista, tipografía serif. Para coaches con narrativa y storytelling.",
    palette: { primary: "#0f172a", accent: "#c084fc", ink: DEFAULT_INK, bg: "#fafaf9" },
    typography: { display: "Playfair Display", body: "Inter Tight" },
    cover: {
      eyebrow: "EDITORIAL · VOL. I",
      programTitle: "El Arte de Construir Músculo",
      programSubtitle: "Una guía de 10 semanas para ganar masa con propósito",
      duration: "10 semanas",
    },
    methodology: "Filosofía de calidad sobre cantidad: cada repetición cuenta como un acto consciente.",
    notes: "Este programa es tanto físico como narrativo. Lleva un cuaderno de progreso, anota sensaciones, no solo números.",
    routine: hyperRoutine,
    cardio: CARDIO_HYPER,
    diet: dietHyper(),
    supplements: SUPP_HYPER,
  },
  // 4
  {
    id: "cut-sharp",
    label: "Cutting Sharp",
    category: "Definición",
    description: "Déficit agresivo + cardio intenso. Para definir manteniendo masa.",
    palette: { primary: "#06b6d4", accent: "#f97316", ink: DEFAULT_INK, bg: DEFAULT_BG },
    typography: { display: "Bricolage Grotesque", body: "Inter Tight" },
    cover: {
      eyebrow: "CUTTING PHASE",
      programTitle: "Sharp Cut",
      programSubtitle: "8 semanas para revelar definición",
      duration: "8 semanas",
    },
    methodology: "Déficit calórico estructurado + alta proteína + cardio mixto LISS/HIIT.",
    notes: "Pésate 1x/sem (mismo día/hora). Mide cintura cada 2 semanas. Si no bajas en 10 días, ajustamos kcal.",
    routine: cutRoutine,
    cardio: CARDIO_CUT,
    diet: dietCut(),
    supplements: SUPP_CUT,
  },
  // 5
  {
    id: "cut-lean",
    label: "Lean Minimal",
    category: "Definición",
    description: "Definición sostenible, déficit suave. Estilo minimal y limpio.",
    palette: { primary: "#10b981", accent: "#0a0a0a", ink: DEFAULT_INK, bg: DEFAULT_BG },
    typography: { display: "Inter Tight", body: "Inter Tight" },
    cover: {
      eyebrow: "LEAN PROGRAM",
      programTitle: "Lean & Clean",
      programSubtitle: "10 semanas de definición sostenible",
      duration: "10 semanas",
    },
    methodology: "Déficit moderado y constante · 5 entrenos + cardio estratégico.",
    notes: "Sé paciente. La pérdida sostenible es 0.5-0.7% del peso corporal por semana. Hidratación + sueño = no opcional.",
    routine: cutRoutine,
    cardio: CARDIO_CUT,
    diet: dietCut(),
    supplements: SUPP_CUT,
  },
  // 6
  {
    id: "power-iron",
    label: "Power Iron",
    category: "Powerlifting",
    description: "Foco en SBD: sentadilla, banca, peso muerto. Periodización en bloques.",
    palette: { primary: "#18181b", accent: "#fbbf24", ink: DEFAULT_INK, bg: DEFAULT_BG },
    typography: { display: "Bricolage Grotesque", body: "JetBrains Mono" },
    cover: {
      eyebrow: "POWERLIFTING · 12 WEEK BLOCK",
      programTitle: "Iron Strength",
      programSubtitle: "Acumulación · Intensificación · Realización",
      duration: "12 semanas",
    },
    methodology: "Bloques de 4 semanas: hipertrofia base → fuerza submáxima → peak y test.",
    notes: "Calentamiento ramp-up obligatorio antes de cada SBD. RPE 7-8 en sets de trabajo, RPE 9 solo en semana de peak.",
    routine: powerRoutine,
    cardio: CARDIO_POWER,
    diet: dietPower(),
    supplements: SUPP_POWER,
  },
  // 7
  {
    id: "power-heritage",
    label: "Strength Heritage",
    category: "Powerlifting",
    description: "Estilo old-school. Tipografía clásica para coaches tradicionales.",
    palette: { primary: "#78350f", accent: "#ea580c", ink: DEFAULT_INK, bg: "#fdf6ec" },
    typography: { display: "Playfair Display", body: "Inter Tight" },
    cover: {
      eyebrow: "STRENGTH · TRADITION",
      programTitle: "Heritage Method",
      programSubtitle: "16 semanas de fuerza al estilo clásico",
      duration: "16 semanas",
    },
    methodology: "Frecuencia 3 con barra base. Filosofía old-school: pocos ejercicios, mucha intensidad acumulada.",
    notes: "Mantén un libro de entrenamiento físico. Pesa la barra cada vez. La fuerza se construye con paciencia y consistencia.",
    routine: powerRoutine,
    cardio: CARDIO_POWER,
    diet: dietPower(),
    supplements: SUPP_POWER,
  },
  // 8
  {
    id: "recomp-balance",
    label: "Recomp Balance",
    category: "Recomp",
    description: "Equilibrio fuerza · hipertrofia · estética. Mantenimiento calórico.",
    palette: { primary: "#6366f1", accent: "#14b8a6", ink: DEFAULT_INK, bg: DEFAULT_BG },
    typography: { display: "Bricolage Grotesque", body: "Inter Tight" },
    cover: {
      eyebrow: "BODY RECOMPOSITION",
      programTitle: "Recomp Balance",
      programSubtitle: "12 semanas para ganar músculo y bajar grasa simultáneo",
      duration: "12 semanas",
    },
    methodology: "Mantenimiento calórico · proteína 2g/kg · 4 entrenos + 2 cardios estratégicos.",
    notes: "La recomp es lenta pero sostenible. No esperes cambios en la balanza, espera cambios en el espejo y la cinta métrica.",
    routine: recompRoutine,
    cardio: CARDIO_RECOMP,
    diet: dietRecomp(),
    supplements: SUPP_RECOMP,
  },
  // 9
  {
    id: "recomp-athletic",
    label: "Hybrid Athletic",
    category: "Recomp",
    description: "Combina fuerza + cardio funcional. Para atletas híbridos modernos.",
    palette: { primary: "#1e40af", accent: "#f59e0b", ink: DEFAULT_INK, bg: DEFAULT_BG },
    typography: { display: "Montserrat", body: "Inter Tight" },
    cover: {
      eyebrow: "HYBRID ATHLETE",
      programTitle: "Athletic Recomp",
      programSubtitle: "10 semanas de fuerza y capacidad aeróbica",
      duration: "10 semanas",
    },
    methodology: "Entrenamiento concurrente: fuerza 4d + cardio aeróbico 2d. Recovery activo crítico.",
    notes: "El concurrente requiere recuperación premium. 8h sueño, hidratación, foam roller. Si fatiga >7/10, baja volumen.",
    routine: recompRoutine,
    cardio: CARDIO_RECOMP,
    diet: dietRecomp(),
    supplements: SUPP_RECOMP,
  },
  // 10
  {
    id: "calis-clean",
    label: "Bodyweight Clean",
    category: "Calistenia",
    description: "Calistenia minimalista. Sin equipo. Progresiones por dificultad.",
    palette: { primary: "#475569", accent: "#84cc16", ink: DEFAULT_INK, bg: DEFAULT_BG },
    typography: { display: "Inter Tight", body: "Inter Tight" },
    cover: {
      eyebrow: "BODYWEIGHT TRAINING",
      programTitle: "Bodyweight Clean",
      programSubtitle: "12 semanas · Sin gym, solo tu cuerpo",
      duration: "12 semanas",
    },
    methodology: "Progresiones lentas dominando cada nivel antes de avanzar. Calidad > cantidad.",
    notes: "Cada repetición es técnica. No ejecutes mal por completar reps. Descansa lo necesario entre sets.",
    routine: calisRoutine,
    cardio: CARDIO_CALIS,
    diet: dietCalis(),
    supplements: SUPP_CALIS,
  },
  // 11
  {
    id: "calis-street",
    label: "Street Workout",
    category: "Calistenia",
    description: "Calistenia urbana intensa. Skills explosivos · muscle-up · planche.",
    palette: { primary: "#18181b", accent: "#ef4444", ink: DEFAULT_INK, bg: DEFAULT_BG },
    typography: { display: "Bricolage Grotesque", body: "JetBrains Mono" },
    cover: {
      eyebrow: "STREET WORKOUT",
      programTitle: "Calle. Barra. Cuerpo.",
      programSubtitle: "10 semanas para skills urbanas avanzadas",
      duration: "10 semanas",
    },
    methodology: "Combinación skill day + fuerza máxima + capacidad explosiva.",
    notes: "Los skills toman meses. Sé constante. Filma cada intento — el video es tu mejor coach.",
    routine: calisRoutine,
    cardio: CARDIO_CALIS,
    diet: dietCalis(),
    supplements: SUPP_CALIS,
  },
  // 12
  {
    id: "femme-glow",
    label: "Femme Glow",
    category: "Mujer Fitness",
    description: "Énfasis glúteo + femoral. Estética editorial femenina.",
    palette: { primary: "#ec4899", accent: "#fde047", ink: DEFAULT_INK, bg: "#fffbf5" },
    typography: { display: "Playfair Display", body: "Inter Tight" },
    cover: {
      eyebrow: "FEMME · GLOW PROGRAM",
      programTitle: "Glow Up",
      programSubtitle: "10 semanas para tu mejor versión",
      duration: "10 semanas",
    },
    methodology: "Foco en cadena posterior y glúteo. Dieta moderada en déficit con proteína alta.",
    notes: "El cambio es gradual. Mide caderas y glúteo cada 2 semanas. Foto frontal/lateral mensual con misma luz.",
    routine: femmeRoutine,
    cardio: CARDIO_FEMME,
    diet: dietFemme(),
    supplements: SUPP_FEMME,
  },
  // 13
  {
    id: "femme-strong",
    label: "Strong Pastel",
    category: "Mujer Fitness",
    description: "Glúteo fuerte + tono general. Paleta pastel suave moderna.",
    palette: { primary: "#c084fc", accent: "#f9a8d4", ink: DEFAULT_INK, bg: "#fdfaff" },
    typography: { display: "Bricolage Grotesque", body: "Montserrat" },
    cover: {
      eyebrow: "STRONG · PASTEL EDITION",
      programTitle: "Strong & Soft",
      programSubtitle: "12 semanas · Fuerte por dentro, suave por fuera",
      duration: "12 semanas",
    },
    methodology: "Fuerza progresiva + acondicionamiento. Más músculo, mejor postura, más energía.",
    notes: "Levantar pesado no te pone musculosa, te pone fuerte. Confía en el proceso y carga la barra.",
    routine: femmeRoutine,
    cardio: CARDIO_FEMME,
    diet: dietFemme(),
    supplements: SUPP_FEMME,
  },
  // 14
  {
    id: "endur-pulse",
    label: "Endurance Pulse",
    category: "Endurance",
    description: "Plan running estructurado · base aeróbica + intervalos.",
    palette: { primary: "#0ea5e9", accent: "#f43f5e", ink: DEFAULT_INK, bg: DEFAULT_BG },
    typography: { display: "Bricolage Grotesque", body: "Inter Tight" },
    cover: {
      eyebrow: "ENDURANCE PROGRAM",
      programTitle: "Pulse Method",
      programSubtitle: "12 semanas hacia tu meta de carrera",
      duration: "12 semanas",
    },
    methodology: "Periodización running clásica: base · build · peak · taper.",
    notes: "Escucha el cuerpo. Si FCR matutina sube >5 bpm, descansa. Hidratación + electrolitos en cada sesión >1h.",
    routine: endurRoutine,
    cardio: CARDIO_ENDUR,
    diet: dietEndur(),
    supplements: SUPP_ENDUR,
  },
  // 15
  {
    id: "begin-friendly",
    label: "Iniciación Amable",
    category: "Iniciación",
    description: "Para alguien que arranca · 3 días suaves full body. Foco en hábito.",
    palette: { primary: "#14b8a6", accent: "#f59e0b", ink: DEFAULT_INK, bg: DEFAULT_BG },
    typography: { display: "Inter Tight", body: "Inter Tight" },
    cover: {
      eyebrow: "BIENVENIDA · NIVEL 1",
      programTitle: "Tus primeros 8 semanas",
      programSubtitle: "Plan suave para empezar a entrenar",
      duration: "8 semanas",
    },
    methodology: "Volumen bajo · técnica primero · RIR 3 · construir hábito antes que rendimiento.",
    notes: "El primer mes el objetivo es ir 3 veces por semana sin falta. La carga importa después. Disfruta el proceso.",
    routine: beginRoutine,
    cardio: CARDIO_BEGIN,
    diet: dietBegin(),
    supplements: SUPP_BEGIN,
  },

  // ====================================================================
  // FEATURE 1 — 10 plantillas nuevas (categorías existentes)
  // ====================================================================

  // 16
  {
    id: "hyper-women",
    label: "Hipertrofia Femenina",
    category: "Mujer Fitness",
    description: "Hipertrofia con énfasis en tren inferior y glúteo, pensada para mujeres.",
    palette: { primary: "#db2777", accent: "#fbbf24", ink: DEFAULT_INK, bg: "#fffbf7" },
    typography: { display: "Bricolage Grotesque", body: "Inter Tight" },
    cover: {
      eyebrow: "HIPERTROFIA FEMENINA",
      programTitle: "Fuerte y Femenina",
      programSubtitle: "12 semanas de hipertrofia con foco en glúteo y piernas",
      duration: "12 semanas",
    },
    methodology: "Hipertrofia progresiva priorizando cadena posterior, sin déficit agresivo.",
    notes: "Levantar pesado construye la forma que buscas. Sé constante con la sobrecarga progresiva y registra cada sesión.",
    routine: femmeRoutine,
    cardio: CARDIO_FEMME,
    diet: dietFemme(),
    supplements: SUPP_FEMME,
  },
  // 17
  {
    id: "cut-shred",
    label: "Shred Extremo",
    category: "Definición",
    description: "Definición avanzada de alta intensidad. Déficit y cardio agresivos.",
    palette: { primary: "#0a0a0a", accent: "#ef4444", ink: DEFAULT_INK, bg: DEFAULT_BG },
    typography: { display: "Bricolage Grotesque", body: "JetBrains Mono" },
    cover: {
      eyebrow: "SHRED PROTOCOL",
      programTitle: "Shred Extremo",
      programSubtitle: "6 semanas para llevar la definición al límite",
      duration: "6 semanas",
    },
    methodology: "Déficit pronunciado + cardio LISS/HIIT diario + alta proteína para blindar la masa magra.",
    notes: "Protocolo demandante: solo para fases cortas. Si energía o sueño caen, ajustamos. Pésate diario, promedia semanal.",
    routine: cutRoutine,
    cardio: CARDIO_CUT,
    diet: dietCut(),
    supplements: SUPP_CUT,
  },
  // 18
  {
    id: "power-raw",
    label: "Raw Strength",
    category: "Powerlifting",
    description: "Fuerza sin equipamiento de apoyo. SBD crudo y accesorios mínimos.",
    palette: { primary: "#1c1917", accent: "#dc2626", ink: DEFAULT_INK, bg: DEFAULT_BG },
    typography: { display: "Bricolage Grotesque", body: "JetBrains Mono" },
    cover: {
      eyebrow: "RAW POWERLIFTING",
      programTitle: "Raw Strength",
      programSubtitle: "12 semanas de fuerza cruda hacia tus máximos",
      duration: "12 semanas",
    },
    methodology: "Frecuencia 3 sobre SBD con ramp-ups largos. Accesorios mínimos, intensidad máxima.",
    notes: "Calentamiento ramp-up obligatorio. Cinturón solo en series pesadas. La técnica nunca se negocia por kilos.",
    routine: powerRoutine,
    cardio: CARDIO_POWER,
    diet: dietPower(),
    supplements: SUPP_POWER,
  },
  // 19
  {
    id: "recomp-glow",
    label: "Recomp Glow",
    category: "Recomp",
    description: "Recomposición estética con vibra editorial. Músculo y definición a la vez.",
    palette: { primary: "#8b5cf6", accent: "#22d3ee", ink: DEFAULT_INK, bg: "#faf9ff" },
    typography: { display: "Playfair Display", body: "Inter Tight" },
    cover: {
      eyebrow: "RECOMP · GLOW EDITION",
      programTitle: "Recomp Glow",
      programSubtitle: "12 semanas para ganar músculo y perder grasa con estética",
      duration: "12 semanas",
    },
    methodology: "Mantenimiento calórico con calidad alimentaria · 4 entrenos + cardio aeróbico ligero.",
    notes: "El glow viene de la consistencia. Mídete con cinta y fotos, no solo con la balanza. Duerme y cuida el estrés.",
    routine: recompRoutine,
    cardio: CARDIO_RECOMP,
    diet: dietRecomp(),
    supplements: SUPP_RECOMP,
  },
  // 20
  {
    id: "calis-flow",
    label: "Movement Flow",
    category: "Calistenia",
    description: "Calistenia fluida orientada al control, movilidad y skills continuos.",
    palette: { primary: "#0d9488", accent: "#f59e0b", ink: DEFAULT_INK, bg: DEFAULT_BG },
    typography: { display: "Inter Tight", body: "Inter Tight" },
    cover: {
      eyebrow: "MOVEMENT TRAINING",
      programTitle: "Movement Flow",
      programSubtitle: "10 semanas de calistenia fluida y control corporal",
      duration: "10 semanas",
    },
    methodology: "Progresiones suaves + trabajo de movilidad y control. Calidad de movimiento sobre carga.",
    notes: "El flow se construye con paciencia. Domina cada nivel antes de avanzar. La movilidad no es opcional, es la base.",
    routine: calisRoutine,
    cardio: CARDIO_CALIS,
    diet: dietCalis(),
    supplements: SUPP_CALIS,
  },
  // 21
  {
    id: "endur-trail",
    label: "Trail & Resistencia",
    category: "Endurance",
    description: "Resistencia para trail running: desnivel, fuerza de piernas y base aeróbica.",
    palette: { primary: "#15803d", accent: "#facc15", ink: DEFAULT_INK, bg: DEFAULT_BG },
    typography: { display: "Bricolage Grotesque", body: "Inter Tight" },
    cover: {
      eyebrow: "TRAIL ENDURANCE",
      programTitle: "Trail & Resistencia",
      programSubtitle: "12 semanas para tu próxima carrera de montaña",
      duration: "12 semanas",
    },
    methodology: "Periodización de resistencia con foco en desnivel + fuerza específica de piernas.",
    notes: "El trail castiga lo que no entrenas: trabaja bajadas y subidas. Hidrátate y lleva electrolitos en sesiones largas.",
    routine: endurRoutine,
    cardio: CARDIO_ENDUR,
    diet: dietEndur(),
    supplements: SUPP_ENDUR,
  },
  // 22
  {
    id: "begin-reset",
    label: "Reset 30 días",
    category: "Iniciación",
    description: "Un mes para reactivarse: hábito, técnica y constancia desde cero.",
    palette: { primary: "#0ea5e9", accent: "#34d399", ink: DEFAULT_INK, bg: DEFAULT_BG },
    typography: { display: "Inter Tight", body: "Inter Tight" },
    cover: {
      eyebrow: "RESET · NIVEL 1",
      programTitle: "Reset 30 días",
      programSubtitle: "Un mes para volver a entrenar con estructura",
      duration: "4 semanas",
    },
    methodology: "Volumen bajo, RIR 3, full body 3x/sem. El objetivo del mes es el hábito, no el rendimiento.",
    notes: "30 días para reactivar el cuerpo. Si vas las 3 veces por semana, ya ganaste. La intensidad llega después.",
    routine: beginRoutine,
    cardio: CARDIO_BEGIN,
    diet: dietBegin(),
    supplements: SUPP_BEGIN,
  },
  // 23
  {
    id: "hyper-aesthetic",
    label: "Estética Clásica",
    category: "Hipertrofia",
    description: "Hipertrofia con estética old-school: proporción, simetría y detalle.",
    palette: { primary: "#44403c", accent: "#d6d3d1", ink: DEFAULT_INK, bg: "#faf9f7" },
    typography: { display: "Playfair Display", body: "Montserrat" },
    cover: {
      eyebrow: "CLASSIC AESTHETICS",
      programTitle: "Estética Clásica",
      programSubtitle: "12 semanas para construir un físico proporcionado",
      duration: "12 semanas",
    },
    methodology: "Hipertrofia clásica buscando proporción y simetría. Volumen progresivo, técnica impecable.",
    notes: "La estética clásica es proporción y detalle. Trabaja tus puntos débiles con énfasis. Paciencia: el físico se esculpe.",
    routine: hyperRoutine,
    cardio: CARDIO_HYPER,
    diet: dietHyper(),
    supplements: SUPP_HYPER,
  },
  // 24
  {
    id: "cut-summer",
    label: "Summer Cut",
    category: "Definición",
    description: "Definición fresca para llegar al verano. Déficit alegre y sostenible.",
    palette: { primary: "#f97316", accent: "#06b6d4", ink: DEFAULT_INK, bg: DEFAULT_BG },
    typography: { display: "Montserrat", body: "Inter Tight" },
    cover: {
      eyebrow: "SUMMER CUT",
      programTitle: "Summer Cut",
      programSubtitle: "10 semanas para llegar listo al verano",
      duration: "10 semanas",
    },
    methodology: "Déficit moderado y disfrutable + cardio mixto. Sostenible para no llegar quemado al verano.",
    notes: "Definir no es sufrir. Déficit moderado, comida que disfrutes, pasos altos. Mide cintura cada 2 semanas.",
    routine: cutRoutine,
    cardio: CARDIO_CUT,
    diet: dietCut(),
    supplements: SUPP_CUT,
  },
  // 25
  {
    id: "recomp-home",
    label: "Recomp en Casa",
    category: "Recomp",
    description: "Recomposición sin gym: mancuernas, bandas y peso corporal en casa.",
    palette: { primary: "#6366f1", accent: "#f472b6", ink: DEFAULT_INK, bg: DEFAULT_BG },
    typography: { display: "Inter Tight", body: "Inter Tight" },
    cover: {
      eyebrow: "HOME RECOMP",
      programTitle: "Recomp en Casa",
      programSubtitle: "10 semanas para recomponer tu cuerpo sin pisar el gym",
      duration: "10 semanas",
    },
    methodology: "Recomposición en casa con mancuernas + bandas. Mantenimiento calórico y sobrecarga progresiva.",
    notes: "Sin gym no hay excusa: mancuernas, bandas y constancia bastan. Aumenta reps o carga cada semana que puedas.",
    routine: homeRoutine,
    cardio: CARDIO_RECOMP,
    diet: dietRecomp(),
    supplements: SUPP_RECOMP,
  },

  // ====================================================================
  // FEATURE 2 — 2 plantillas "dark editorial deportivo"
  // ====================================================================

  // 26
  {
    id: "pro-blackout",
    label: "Blackout Pro",
    category: "Powerlifting",
    description: "Estilo editorial deportivo oscuro. Fondo negro, verde neón, tipografía condensada.",
    palette: { primary: "#A4E830", accent: "#A4E830", ink: "#ffffff", bg: "#0a0a0a" },
    typography: { display: "Oswald", body: "JetBrains Mono" },
    cover: {
      eyebrow: "POWERLIFTING · PRO BLOCK",
      programTitle: "BLACKOUT",
      programSubtitle: "12 semanas de fuerza pura en modo oscuro",
      duration: "12 semanas",
    },
    methodology: "Bloques de fuerza sobre SBD con intensidad creciente. Mentalidad pro, ejecución sin concesiones.",
    notes: "Modo blackout: cero distracciones. Ramp-up obligatorio, RPE controlado, registro de cada serie. La fuerza es un oficio.",
    routine: powerRoutine,
    cardio: CARDIO_POWER,
    diet: dietPower(),
    supplements: SUPP_POWER,
  },
  // 27
  {
    id: "pro-neon",
    label: "Neón Atleta",
    category: "Hipertrofia",
    description: "Hipertrofia con estética dark deportiva. Negro azulado y verde neón eléctrico.",
    palette: { primary: "#00ff9d", accent: "#00ff9d", ink: "#ffffff", bg: "#0a0e14" },
    typography: { display: "Oswald", body: "JetBrains Mono" },
    cover: {
      eyebrow: "HIPERTROFIA · NEON ATHLETE",
      programTitle: "NEÓN ATLETA",
      programSubtitle: "10 semanas de hipertrofia con mentalidad de atleta",
      duration: "10 semanas",
    },
    methodology: "Hipertrofia de alta intensidad con split push/pull/legs. Volumen progresivo y técnica de atleta.",
    notes: "Mentalidad de atleta: cada serie cuenta. Controla la excéntrica, cumple los RIR y registra todo. El neón se gana.",
    routine: hyperRoutine,
    cardio: CARDIO_HYPER,
    diet: dietHyper(),
    supplements: SUPP_HYPER,
  },
];

// ----------------------------------------------------------------------
// Portadas visuales por plantilla — imágenes Unsplash + layout + overlay.
// Cada plantilla apunta a una imagen real de fitness y un layout distinto.
// Si una URL falla en runtime, el navegador muestra el background-color del
// contenedor (que ya está tematizado), así que la UX se degrada graceful.
// ----------------------------------------------------------------------

const COVERS: Record<string, TemplateCover> = {
  // 1 — Hipertrofia Clásico · deadlift
  "hyper-classic": {
    imageUrl: UNSPLASH("1546483875-ad9014c88eba"),
    overlay: "dark-gradient",
    layout: "hero-bottom",
  },
  // 2 — Hipertrofia Bold · mancuernas oscuras, magazine cover
  "hyper-bold": {
    imageUrl: UNSPLASH("1517836357463-d25dfeac3438"),
    overlay: "duotone",
    layout: "magazine",
  },
  // 3 — Hipertrofia Editorial · gym wide, split vertical
  "hyper-editorial": {
    imageUrl: UNSPLASH("1571019613454-1cb2f99b2d8b"),
    overlay: "none",
    layout: "split-vertical",
  },
  // 4 — Cutting Sharp · gym mirror, hero top
  "cut-sharp": {
    imageUrl: UNSPLASH("1599058917212-d750089bc07e"),
    overlay: "color-tint",
    layout: "hero-top",
  },
  // 5 — Lean Minimal · comida saludable, split diagonal
  "cut-lean": {
    imageUrl: UNSPLASH("1583454110551-21f2fa2afe61"),
    overlay: "none",
    layout: "split-diagonal",
  },
  // 6 — Power Iron · deadlift, frame border
  "power-iron": {
    imageUrl: UNSPLASH("1546483875-ad9014c88eba"),
    overlay: "dark-gradient",
    layout: "frame-border",
  },
  // 7 — Strength Heritage · gym contraste alto, magazine
  "power-heritage": {
    imageUrl: UNSPLASH("1571019613454-1cb2f99b2d8b"),
    overlay: "duotone",
    layout: "magazine",
  },
  // 8 — Recomp Balance · kettlebell, split vertical
  "recomp-balance": {
    imageUrl: UNSPLASH("1540497077202-7c8a3999166f"),
    overlay: "color-tint",
    layout: "split-vertical",
  },
  // 9 — Hybrid Athletic · pista, hero-top
  "recomp-athletic": {
    imageUrl: UNSPLASH("1574680096145-d05b474e2155"),
    overlay: "dark-gradient",
    layout: "hero-top",
  },
  // 10 — Bodyweight Clean · minimal-icon con anillos (calistenia)
  "calis-clean": {
    imageUrl: UNSPLASH("1605296867424-35fc25c9212a"),
    overlay: "none",
    layout: "minimal-icon",
    iconSvg: "M12 2v6m0 8v6M4 12h6m4 0h6", // cruz/objetivo geométrico
  },
  // 11 — Street Workout · cuerda, hero-bottom
  "calis-street": {
    imageUrl: UNSPLASH("1534438097545-a2c22c57f2ad"),
    overlay: "dark-gradient",
    layout: "hero-bottom",
  },
  // 12 — Femme Glow · mujer fitness, polaroid (URL anterior 404, reemplazada 2026-05-13)
  "femme-glow": {
    imageUrl: UNSPLASH("1518611012118-696072aa579a"),
    overlay: "none",
    layout: "polaroid",
  },
  // 13 — Strong Pastel · mujer levantando, hero-bottom con duotone
  "femme-strong": {
    imageUrl: UNSPLASH("1581009146145-b5ef050c2e1e"),
    overlay: "duotone",
    layout: "hero-bottom",
  },
  // 14 — Endurance Pulse · pista, split-diagonal
  "endur-pulse": {
    imageUrl: UNSPLASH("1574680096145-d05b474e2155"),
    overlay: "color-tint",
    layout: "split-diagonal",
  },
  // 15 — Iniciación Amable · minimal-icon con zapatos / flecha-up
  "begin-friendly": {
    imageUrl: UNSPLASH("1534438327276-14e5300c3a48"),
    overlay: "none",
    layout: "minimal-icon",
    iconSvg: "M3 17l6-6 4 4 8-8M14 7h7v7",
  },

  // --- FEATURE 1 — 10 plantillas nuevas ---

  // 16 — Hipertrofia Femenina · mujer entrenando, polaroid
  "hyper-women": {
    imageUrl: UNSPLASH("1518611012118-696072aa579a"),
    overlay: "none",
    layout: "polaroid",
  },
  // 17 — Shred Extremo · gym oscuro, magazine duotone
  "cut-shred": {
    imageUrl: UNSPLASH("1599058917212-d750089bc07e"),
    overlay: "duotone",
    layout: "magazine",
  },
  // 18 — Raw Strength · barra/deadlift, frame border
  "power-raw": {
    imageUrl: UNSPLASH("1534438097545-a2c22c57f2ad"),
    overlay: "dark-gradient",
    layout: "frame-border",
  },
  // 19 — Recomp Glow · gym wide, split vertical
  "recomp-glow": {
    imageUrl: UNSPLASH("1571019613454-1cb2f99b2d8b"),
    overlay: "none",
    layout: "split-vertical",
  },
  // 20 — Movement Flow · calistenia, hero-top
  "calis-flow": {
    imageUrl: UNSPLASH("1605296867424-35fc25c9212a"),
    overlay: "color-tint",
    layout: "hero-top",
  },
  // 21 — Trail & Resistencia · corredor, split-diagonal
  "endur-trail": {
    imageUrl: UNSPLASH("1574680096145-d05b474e2155"),
    overlay: "dark-gradient",
    layout: "split-diagonal",
  },
  // 22 — Reset 30 días · minimal-icon flecha de progreso
  "begin-reset": {
    imageUrl: UNSPLASH("1534438327276-14e5300c3a48"),
    overlay: "none",
    layout: "minimal-icon",
    iconSvg: "M3 12a9 9 0 1 0 9-9M3 12V6m0 6h6",
  },
  // 23 — Estética Clásica · gym contraste alto, hero-bottom
  "hyper-aesthetic": {
    imageUrl: UNSPLASH("1517836357463-d25dfeac3438"),
    overlay: "color-tint",
    layout: "hero-bottom",
  },
  // 24 — Summer Cut · comida fresca / gym, split-diagonal
  "cut-summer": {
    imageUrl: UNSPLASH("1583454110551-21f2fa2afe61"),
    overlay: "color-tint",
    layout: "split-diagonal",
  },
  // 25 — Recomp en Casa · kettlebell / entreno en casa, hero-bottom
  "recomp-home": {
    imageUrl: UNSPLASH("1540497077202-7c8a3999166f"),
    overlay: "dark-gradient",
    layout: "hero-bottom",
  },

  // --- FEATURE 2 — 2 plantillas "dark editorial deportivo" (estilo Fredy) ---

  // 26 — Blackout Pro · barra pesada, magazine + dark-gradient
  "pro-blackout": {
    imageUrl: UNSPLASH("1534438097545-a2c22c57f2ad"),
    overlay: "dark-gradient",
    layout: "magazine",
  },
  // 27 — Neón Atleta · mancuernas/gym oscuro, magazine + dark-gradient
  "pro-neon": {
    imageUrl: UNSPLASH("1517836357463-d25dfeac3438"),
    overlay: "dark-gradient",
    layout: "magazine",
  },
};

// ----------------------------------------------------------------------
// Construye el array final de PlanTemplate
// ----------------------------------------------------------------------

export const TEMPLATES: PlanTemplate[] = SPECS.map((spec): PlanTemplate => {
  const content: TemplateContent = {
    cover: {
      eyebrow: spec.cover.eyebrow,
      programTitle: spec.cover.programTitle,
      programSubtitle: spec.cover.programSubtitle,
      duration: spec.cover.duration,
    },
    client: {
      goal: spec.category,
      level: spec.category === "Iniciación" ? "Principiante" : "Intermedio",
    },
    coachBio: {
      methodology: spec.methodology,
    },
    routine: spec.routine("Semana 1"),
    cardio: spec.cardio,
    diet: spec.diet,
    supplements: spec.supplements,
    notes: buildNotes(spec.notes),
  };
  // Las plantillas "dark editorial" se reconocen por tener un `bg` oscuro en
  // su paleta — su `applyDefaults` marca `brand.theme = "dark"` para que las
  // secciones del plan se rendericen con fondo oscuro.
  const theme: "light" | "dark" = isDarkPalette(spec.palette) ? "dark" : "light";
  return {
    id: spec.id,
    label: spec.label,
    category: spec.category,
    description: spec.description,
    palette: spec.palette,
    typography: spec.typography,
    cover: COVERS[spec.id],
    applyDefaults: makeApply(spec.id, spec.palette, content, theme, COVERS[spec.id], spec.typography),
  };
});

/**
 * Aplica SOLO el estilo visual de una plantilla a un plan ya existente —
 * cambia template id + palette + tipografía + theme/density SIN tocar las
 * secciones (rutina, dieta, cardio, suplementos, notas, datos del cliente).
 *
 * Caso de uso: el coach armó un plan completo con plantilla "femenina rosa"
 * y quiere reusarlo con plantilla "masculina azul" sin perder el contenido.
 * Llamar `applyDefaults(plan)` lo arruinaría porque sobreescribe sections.
 * Este helper toca solo lo visual.
 *
 * Lo que se preserva en `plan.brand`:
 *   - name, tagline, logoUrl, logoLetters, ig, web, phone (datos de marca del coach)
 *   - watermark, density (preferencias del coach por plan)
 *   - fieldStyles[fieldPath] si existieran (estilos por campo persistidos)
 *
 * Lo que se actualiza:
 *   - plan.template (id de la plantilla nueva)
 *   - plan.brand.primary, accent (paleta nueva)
 *   - plan.brand.displayFont, bodyFont (tipografía nueva — full names; fontStack resuelve)
 *   - plan.brand.theme ("dark" si la nueva paleta es oscura, sino "light")
 *
 * Devuelve UNA NUEVA referencia (no muta el plan original) — apta para usar
 * directo en setState/updatePlan.
 */
export function applyTemplateStyleOnly(plan: PlanDoc, tpl: PlanTemplate): PlanDoc {
  const newTheme: "light" | "dark" = isDarkPalette(tpl.palette) ? "dark" : "light";
  const cover = tpl.cover;
  return {
    ...plan,
    template: tpl.id,
    brand: {
      ...(plan.brand || {}),
      primary: tpl.palette.primary,
      accent: tpl.palette.accent,
      displayFont: tpl.typography.display,
      bodyFont: tpl.typography.body,
      theme: newTheme,
    },
    // Cambiar plantilla también cambia la PORTADA VISUAL (imagen + overlay) —
    // el coach quiere el estilo completo, no solo los colores. Si la plantilla
    // nueva no tiene cover (ej. "en blanco"), se limpia la imagen y vuelve al
    // layout editorial. El resto del contenido (rutina, dieta…) NO se toca.
    sections: plan.sections.map((sec): PlanSection => {
      if (sec.type !== "cover") return sec;
      return {
        ...sec,
        data: {
          ...sec.data,
          imageUrl: cover?.imageUrl,
          imagePosition: cover?.imagePosition,
          overlay: cover?.overlay,
        },
      };
    }),
  };
}

/** Heurística: una paleta es "dark" si su fondo es notablemente oscuro. */
function isDarkPalette(p: TemplatePalette): boolean {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(p.bg);
  if (!m) return false;
  const r = parseInt(m[1], 16);
  const g = parseInt(m[2], 16);
  const b = parseInt(m[3], 16);
  // Luminancia perceptual aproximada (0-255). Umbral conservador.
  const lum = 0.299 * r + 0.587 * g + 0.114 * b;
  return lum < 60;
}

// ----------------------------------------------------------------------
// Lookup helper
// ----------------------------------------------------------------------

export function getTemplate(id: string | null | undefined): PlanTemplate | undefined {
  if (!id) return undefined;
  return TEMPLATES.find((t) => t.id === id);
}
